/* Mobile teacher — "Faire l'appel" (student attendance / présences élèves)
 * Path: /m/teacher/rollcall
 * Flow:
 *   1. Pick date (default today) + class (from `me.classes`)
 *   2. If cycle in {maternelle, primaire} → daily mode, no slot picker
 *      Else → show today's timetable slots for THIS teacher, pick one
 *   3. Show student list with 4 colored status buttons per student
 *   4. Save → bulk POST /student-attendance/rollcall
 */
import { useCallback, useEffect, useState } from 'react';
import { studentAttendanceApi } from '../../../lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { FloppyDisk, ArrowLeft, ClipboardText, CheckCircle, XCircle, Clock, ShieldCheck } from '@phosphor-icons/react';

const STATUS_META = {
  present: { label: 'PRÉSENT', bg: 'bg-emerald-500', text: 'text-white', border: 'border-emerald-600', Icon: CheckCircle },
  absent:  { label: 'ABSENT',  bg: 'bg-red-500',     text: 'text-white', border: 'border-red-600',     Icon: XCircle },
  retard:  { label: 'RETARD',  bg: 'bg-yellow-500',  text: 'text-white', border: 'border-yellow-600',  Icon: Clock },
  excuse:  { label: 'EXCUSÉ',  bg: 'bg-black',       text: 'text-white', border: 'border-black',       Icon: ShieldCheck },
};
const STATUS_KEYS = ['present', 'absent', 'retard', 'excuse'];

const todayStr = () => new Date().toISOString().slice(0, 10);

const TeacherRollcall = ({ me }) => {
  const [step, setStep] = useState('pick');                    // 'pick' | 'call'
  const [date, setDate] = useState(todayStr());
  const [selectedClass, setSelectedClass] = useState('');
  const [slots, setSlots] = useState([]);
  const [selectedSlotId, setSelectedSlotId] = useState('');
  const [rosterData, setRosterData] = useState(null);          // {students, statuses, cycle, mode, slot_info}
  const [statuses, setStatuses] = useState({});                // studentId -> statusKey
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const classes = me?.classes || [];

  // Auto-fill single-class teachers
  useEffect(() => {
    if (classes.length === 1 && !selectedClass) setSelectedClass(classes[0]);
  }, [classes, selectedClass]);

  // Load slots for the picked date (needed as we may not know the class cycle yet)
  const loadSlots = useCallback(async () => {
    try {
      const { data } = await studentAttendanceApi.teacherSlots(date);
      setSlots(data.slots || []);
    } catch (e) {
      setSlots([]);
    }
  }, [date]);
  useEffect(() => { loadSlots(); }, [loadSlots]);

  // Slots filtered to the picked class
  const classSlots = slots.filter((s) => !selectedClass || s.class_name === selectedClass);

  const loadRoster = async () => {
    if (!selectedClass) {
      toast.error('Choisissez une classe.');
      return;
    }
    setLoading(true);
    try {
      const { data } = await studentAttendanceApi.getClass(selectedClass, date, selectedSlotId || undefined);
      // Secondaire cycle without slot picked → force selection first
      if (data.attendance_mode === 'per_slot' && !selectedSlotId) {
        toast.info('Sélectionnez un créneau d\'emploi du temps.');
        setLoading(false);
        setRosterData(data); // still set to reveal slot picker
        return;
      }
      setRosterData(data);
      setStatuses({ ...(data.statuses || {}) });
      setStep('call');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur au chargement de la classe');
    } finally {
      setLoading(false);
    }
  };

  const markAll = (statusKey) => {
    if (!rosterData) return;
    const next = { ...statuses };
    rosterData.students.forEach((s) => { next[s.id] = statusKey; });
    setStatuses(next);
  };

  const setOne = (studentId, statusKey) => {
    setStatuses((prev) => ({ ...prev, [studentId]: statusKey }));
  };

  const save = async () => {
    if (!rosterData) return;
    const entries = Object.entries(statuses)
      .filter(([, v]) => STATUS_KEYS.includes(v))
      .map(([student_id, status]) => ({ student_id, status }));
    if (entries.length === 0) {
      toast.warning('Aucun statut à enregistrer.');
      return;
    }
    setSaving(true);
    try {
      const { data } = await studentAttendanceApi.saveRollcall({
        class_name: selectedClass,
        date,
        slot_id: selectedSlotId || undefined,
        entries,
      });
      toast.success(`Appel enregistré (${data.saved} élève${data.saved > 1 ? 's' : ''})`);
      if (data.pushed_absents > 0) {
        toast.info(`${data.pushed_absents} parent(s) notifié(s) de l'absence.`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  // ======================= UI =======================

  if (step === 'pick') {
    return (
      <div className="p-4 space-y-4" data-testid="rollcall-picker">
        <div className="flex items-center gap-2">
          <ClipboardText size={24} className="text-emerald-600" weight="duotone" />
          <h1 className="font-outfit text-xl font-bold">Faire l&apos;appel</h1>
        </div>

        <Card className="border border-[#E2E8F0] shadow-none">
          <CardContent className="p-4 space-y-3">
            <div>
              <label className="text-sm font-medium text-[#475569]">Date</label>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                data-testid="rollcall-date-input"
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#475569]">Classe</label>
              <select
                value={selectedClass}
                onChange={(e) => { setSelectedClass(e.target.value); setSelectedSlotId(''); }}
                className="mt-1 w-full h-10 rounded-md border border-[#E2E8F0] px-3 text-sm bg-white"
                data-testid="rollcall-class-select"
              >
                <option value="">— Choisir une classe —</option>
                {classes.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            {classSlots.length > 0 && (
              <div>
                <label className="text-sm font-medium text-[#475569]">
                  Créneau (secondaire) — <span className="text-xs text-[#94A3B8]">optionnel pour maternelle/primaire</span>
                </label>
                <select
                  value={selectedSlotId}
                  onChange={(e) => setSelectedSlotId(e.target.value)}
                  className="mt-1 w-full h-10 rounded-md border border-[#E2E8F0] px-3 text-sm bg-white"
                  data-testid="rollcall-slot-select"
                >
                  <option value="">— Journée entière —</option>
                  {classSlots.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.start_time}–{s.end_time} · {s.subject}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <Button
              onClick={loadRoster}
              disabled={loading || !selectedClass}
              className="w-full bg-emerald-600 hover:bg-emerald-700 mt-2"
              data-testid="rollcall-load-btn"
            >
              {loading ? 'Chargement…' : 'Charger la liste'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // step === 'call'
  const total = rosterData?.students?.length || 0;
  const counts = STATUS_KEYS.reduce((acc, k) => {
    acc[k] = Object.values(statuses).filter((v) => v === k).length;
    return acc;
  }, {});

  return (
    <div className="p-3 pb-24 space-y-3" data-testid="rollcall-call">
      {/* Sticky header */}
      <div className="sticky top-0 z-10 -mx-3 px-3 py-2 bg-white border-b border-[#E2E8F0]">
        <div className="flex items-center justify-between gap-2">
          <button
            onClick={() => setStep('pick')}
            className="flex items-center gap-1 text-sm text-[#475569] hover:text-[#0F172A]"
            data-testid="rollcall-back-btn"
          >
            <ArrowLeft size={16} /> Retour
          </button>
          <div className="text-xs text-[#64748B] text-right">
            <div className="font-semibold text-[#0F172A]">{selectedClass}</div>
            <div>
              {date}
              {rosterData?.slot_info && ` · ${rosterData.slot_info.start_time}–${rosterData.slot_info.end_time} · ${rosterData.slot_info.subject}`}
            </div>
          </div>
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5 text-[11px]">
          {STATUS_KEYS.map((k) => {
            const m = STATUS_META[k];
            return (
              <button
                key={k}
                onClick={() => markAll(k)}
                className={`${m.bg} ${m.text} px-2 py-1 rounded-full font-semibold`}
                data-testid={`rollcall-markall-${k}`}
              >
                Tous {m.label} · {counts[k]}
              </button>
            );
          })}
          <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-600">
            Total {total}
          </span>
        </div>
      </div>

      {/* Student list */}
      <div className="space-y-2">
        {(rosterData?.students || []).map((s) => {
          const cur = statuses[s.id];
          return (
            <Card key={s.id} className="border border-[#E2E8F0] shadow-none" data-testid={`rollcall-row-${s.id}`}>
              <CardContent className="p-3 space-y-2">
                <div className="flex items-center gap-2">
                  {s.photo_base64 ? (
                    <img src={s.photo_base64} alt="" className="w-9 h-9 rounded-full object-cover" />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-[#F1F5F9] flex items-center justify-center text-xs font-bold text-[#64748B]">
                      {(s.first_name?.[0] || '') + (s.last_name?.[0] || '')}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-[#0F172A] truncate">
                      {s.first_name} {s.last_name}
                    </p>
                  </div>
                  {cur && (
                    <span
                      className={`${STATUS_META[cur].bg} ${STATUS_META[cur].text} px-2 py-0.5 rounded-full text-[10px] font-bold`}
                      data-testid={`rollcall-current-${s.id}`}
                    >
                      {STATUS_META[cur].label}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-4 gap-1.5">
                  {STATUS_KEYS.map((k) => {
                    const m = STATUS_META[k];
                    const active = cur === k;
                    const Icon = m.Icon;
                    return (
                      <button
                        key={k}
                        onClick={() => setOne(s.id, k)}
                        className={`h-11 rounded-md text-xs font-bold flex flex-col items-center justify-center transition ${
                          active
                            ? `${m.bg} ${m.text} border-2 ${m.border} shadow`
                            : 'bg-white border-2 border-[#E2E8F0] text-[#94A3B8] hover:border-slate-400'
                        }`}
                        data-testid={`rollcall-btn-${s.id}-${k}`}
                      >
                        <Icon size={14} weight={active ? 'fill' : 'regular'} />
                        <span className="mt-0.5">{m.label}</span>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Fixed save button */}
      <div className="fixed left-0 right-0 bottom-16 px-3 z-10">
        <Button
          onClick={save}
          disabled={saving}
          className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 shadow-lg"
          data-testid="rollcall-save-btn"
        >
          <FloppyDisk size={18} className="mr-2" />
          {saving ? 'Enregistrement…' : `Enregistrer l'appel (${Object.keys(statuses).length}/${total})`}
        </Button>
      </div>
    </div>
  );
};

export default TeacherRollcall;
